import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { isEmail } from 'class-validator';
import React, { useState } from 'react';

import { ChannelInput, ChannelType, SlackChannel, useDomainSlackChannelsQuery } from '../../../../../graphql/gen/types';
import { spacings } from '../../../../../theme';

type Props = {
  channels: ChannelInput[];
  onAddChannel: (channel: ChannelInput) => void;
  onRemoveChannel: (channel: ChannelInput) => void;
};

type State =
  | { channelType: 'slack'; slackQuery: string }
  | {
      channelType: 'email';
      email: string;
    }
  | { channelType: '' };

export const PlexChannelSection = React.memo(
  ({ channels, onAddChannel, onRemoveChannel }: Props): React.ReactElement => {
    const classes = useStyles();
    const [state, setState] = useState<State>({ channelType: '' });
    const { data, loading } = useDomainSlackChannelsQuery();
    // Only keep domain channels that have not already been included
    const domainChannels = data?.currentUser?.domain?.slackChannels?.results.filter(
      (c) => !channels.find((ch) => c.id === ch.externalId)
    );

    const emailError = state.channelType === 'email' && state.email !== '' && !isEmail(state.email);

    const handleTypeChange = (
      event: React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
      }>
    ): void => {
      switch (event.target.value) {
        case 'email': {
          setState({ channelType: 'email', email: '' });
          break;
        }
        case 'slack': {
          setState({ channelType: 'slack', slackQuery: '' });
          break;
        }
        default: {
          throw new Error('unknown channel type');
        }
      }
    };

    const handleQueryChange = (_: React.ChangeEvent<unknown>, value: string): void => {
      setState({ channelType: 'slack', slackQuery: value });
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setState({ channelType: 'email', email: event.currentTarget.value });
    };

    const typeToString = (type: ChannelType): string => {
      switch (type) {
        case ChannelType.Slack:
          return 'Slack';
        case ChannelType.Gmail:
          return 'Email';
      }
    };

    const handleAddSlackChannel = (_: React.ChangeEvent<unknown>, value: SlackChannel | null): void => {
      if (value) {
        onAddChannel({
          externalId: value.id,
          name: value.name,
          type: ChannelType.Slack,
        });
      }
      setState({ channelType: '' });
    };

    const handleAddEmailChannel = (): void => {
      if (state.channelType === 'email' && state.email !== '' && !emailError) {
        onAddChannel({
          externalId: state.email,
          type: ChannelType.Gmail,
        });
        setState({ channelType: '' });
      }
    };

    return (
      <div className={classes.root}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell className={classes.actionColumn} />
            </TableRow>
          </TableHead>
          <TableBody>
            {channels.map((c) => (
              <TableRow key={c.id || c.externalId}>
                <TableCell>{typeToString(c.type)}</TableCell>
                <TableCell>{c.type === ChannelType.Slack ? `#${c.name}` : c.externalId}</TableCell>
                <TableCell>
                  <IconButton onClick={(): void => onRemoveChannel(c)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className={classes.searchFormContainer}>
          <FormControl className={classes.formControl} variant="outlined">
            <InputLabel id="channel-select-label">Add new channel</InputLabel>
            <Select
              labelId="channel-select-label"
              id="channel-select"
              value={state.channelType}
              className={classes.channelSelect}
              onChange={handleTypeChange}
              label="Add new channel"
            >
              <MenuItem value="slack">Slack</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </Select>
          </FormControl>
          {state.channelType === 'email' && (
            <TextField
              error={emailError}
              label={emailError ? 'Incorrect email' : 'Add email'}
              variant="outlined"
              className={classes.typeTextField}
              value={state.email}
              onKeyDown={(e): void => {
                if (e.key === 'Enter') handleAddEmailChannel();
              }}
              onChange={handleEmailChange}
            />
          )}
          {state.channelType === 'email' && (
            <div>
              <Button
                variant="contained"
                disabled={state.channelType === 'email' && (state.email === '' || emailError)}
                onClick={handleAddEmailChannel}
              >
                Add
              </Button>
            </div>
          )}
          {state.channelType === 'slack' && loading && (
            <CircularProgress className={classes.progress} color="inherit" size={20} />
          )}
          {state.channelType === 'slack' && !loading && (
            <Autocomplete
              clearOnEscape
              noOptionsText="No such channel"
              onChange={handleAddSlackChannel}
              value={null}
              options={domainChannels || []}
              onInputChange={handleQueryChange}
              inputValue={state.slackQuery}
              getOptionLabel={(option): string => `#${option.name}`}
              renderInput={(params: TextFieldProps): React.ReactElement => (
                <TextField {...params} label="Slack channel" variant="outlined" className={classes.typeTextField} />
              )}
            />
          )}
        </div>
      </div>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: spacings.xlarge,
    marginRight: spacings.xlarge,
    marginBottom: spacings.normal,
  },
  progress: { margin: theme.spacing(2) },
  actionColumn: { width: '12px' },
  typeTextField: { width: 300, marginRight: theme.spacing(1) },
  searchFormContainer: { display: 'flex', marginTop: theme.spacing(2), alignItems: 'center' },
  formControl: {
    marginRight: theme.spacing(1),
  },
  channelSelect: {
    minWidth: 300,
  },
}));
